import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ModelSalesFunnelStageLeads } from '@app/api/SalesFunnels';
import { useGetSalesFunnelLeadsMutation } from '@app/api/SalesFunnels/api';
import { useUserPrint } from '@app/hooks';
import { useLeadsRegistryFilterData } from '@app/pages/Leads/LeadsRegistryPage/hooks';
import { useLeadsRegistryScopedFilters } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistryFilters';
import { useLeadsRegistrySettings } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistrySettings';
import { buildLeadsRegistrySearchFilter } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/buildLeadsRegistrySearchFilter';
import { buildLeadsRegistrySearchSignature } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/buildLeadsRegistrySearchSignature';
import { LEADS_SCROLL_LOAD_THRESHOLD_PX } from '@app/pages/Leads/LeadsRegistryPage/model/constants';
import { normalizeLeadsPortionSize } from '@app/pages/Leads/LeadsRegistryPage/utils';
import {
  normalizeLeadsSalesFunnelSelection,
  resolveLeadsSalesFunnelSelection,
} from '@app/pages/Leads/model/salesFunnelSelection';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { mergeSalesFunnelStageLeads } from '../model/adapters/mergeSalesFunnelStageLeads';
import {
  getLeadsFunnelsBoardSnapshot,
  setLeadsFunnelsBoardSnapshot,
} from '../model/leadsFunnelsBoardSnapshot';

import { useLeadsFunnelDnd } from './useLeadsFunnelDnd';

import type { LeadsFunnelBoardStage } from '../model/types';
import type { UIEvent } from 'react';

export const useLeadsFunnelsPage = () => {
  const { params } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  const { showNotification } = useNotification();
  const { filters, onSalesFunnelChange } =
    useLeadsRegistryScopedFilters('funnels');
  const { loadBatchSize } = useLeadsRegistrySettings('funnels');
  const { funnels, isFilterDataLoading } = useLeadsRegistryFilterData();
  const [getSalesFunnelLeads] = useGetSalesFunnelLeadsMutation();

  const [stageLeads, setStageLeads] = useState<ModelSalesFunnelStageLeads[]>(
    []
  );
  const [requestedMaxSize, setRequestedMaxSize] =
    useState<number>(loadBatchSize);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const requestIdRef = useRef(0);
  const isLoadMoreRequestedRef = useRef(false);
  const isResettingPaginationRef = useRef(false);

  const effectiveFunnelSelection = useMemo(
    () =>
      resolveLeadsSalesFunnelSelection(funnels, {
        salesFunnelId: filters.salesFunnelId,
        funnelStages: filters.funnelStages,
      }),
    [filters.funnelStages, filters.salesFunnelId, funnels]
  );
  const effectiveSalesFunnelId = effectiveFunnelSelection.salesFunnelId;

  useEffect(() => {
    const normalizedSelection = normalizeLeadsSalesFunnelSelection(funnels, {
      salesFunnelId: filters.salesFunnelId,
      funnelStages: filters.funnelStages,
    });

    if (
      normalizedSelection.salesFunnelId === filters.salesFunnelId &&
      normalizedSelection.funnelStages === filters.funnelStages
    ) {
      return;
    }

    onSalesFunnelChange(
      normalizedSelection.salesFunnelId,
      normalizedSelection.funnelStages
    );
  }, [
    filters.funnelStages,
    filters.salesFunnelId,
    funnels,
    onSalesFunnelChange,
  ]);

  const selectedFunnel = useMemo(
    () =>
      effectiveSalesFunnelId === null
        ? null
        : funnels.find((funnel) => funnel.Id === effectiveSalesFunnelId) ??
          null,
    [effectiveSalesFunnelId, funnels]
  );

  const selectedFunnelStageIds = useMemo(
    () => (selectedFunnel?.Stages ?? []).map((stage) => stage.Id),
    [selectedFunnel?.Stages]
  );

  const effectiveStageIds = useMemo(() => {
    if (!selectedFunnel) return [];
    if (effectiveFunnelSelection.funnelStages.length === 0)
      return selectedFunnelStageIds;

    const selectedFunnelStageIdsSet = new Set(selectedFunnelStageIds);

    return effectiveFunnelSelection.funnelStages.filter((stageId) =>
      selectedFunnelStageIdsSet.has(stageId)
    );
  }, [
    effectiveFunnelSelection.funnelStages,
    selectedFunnel,
    selectedFunnelStageIds,
  ]);

  const hasSelectedFunnel =
    effectiveSalesFunnelId !== null &&
    effectiveSalesFunnelId > 0 &&
    effectiveStageIds.length > 0;

  const searchFilter = useMemo(
    () => ({
      ...buildLeadsRegistrySearchFilter({
        ...filters,
        funnelStages: effectiveStageIds,
      }),
      MaxItems: normalizeLeadsPortionSize(requestedMaxSize),
    }),
    [effectiveStageIds, filters, requestedMaxSize]
  );

  // Deterministic, size-independent identity of the current board request
  // (funnel + effective filters/stages). Reused to decide whether the
  // cross-navigation board snapshot can be reused instead of re-requesting
  // `GetSalesFunnelLeads` when returning from the Lead Card with an unchanged
  // context. Array fields are sorted by `buildLeadsRegistrySearchSignature`, so
  // equivalent filters always serialize to the same string.
  const boardSignature = useMemo(
    () =>
      JSON.stringify({
        salesFunnelId: effectiveSalesFunnelId,
        filter: buildLeadsRegistrySearchSignature(searchFilter),
      }),
    [effectiveSalesFunnelId, searchFilter]
  );

  const totalCount = useMemo(
    () => stageLeads.reduce((sum, stage) => sum + stage.TotalLeads, 0),
    [stageLeads]
  );

  const loadedCount = useMemo(
    () =>
      stageLeads.reduce((sum, stage) => sum + (stage.Leads ?? []).length, 0),
    [stageLeads]
  );

  const hasMoreLeads = hasLoaded && totalCount > loadedCount;

  // Per-stage board cache keyed by the ModelSalesFunnelStageLeads object ref.
  // impl-A makes unchanged stage refs stable across load-more and DnD previews;
  // this cache then reuses the same LeadsFunnelBoardStage object for those
  // stages, so LeadsFunnelStageColumn.memo() bails out without re-rendering.
  const stageBoardCacheRef = useRef(
    new WeakMap<ModelSalesFunnelStageLeads, LeadsFunnelBoardStage>()
  );

  const board = useMemo(() => {
    const stageCache = stageBoardCacheRef.current;
    const funnelName =
      funnels.find((f) => f.Id === effectiveSalesFunnelId)?.Name ??
      (effectiveSalesFunnelId
        ? `Воронка ${effectiveSalesFunnelId}`
        : 'Воронка не выбрана');

    const stages = stageLeads.map((stage) => {
      const cached = stageCache.get(stage);
      if (cached !== undefined && cached.funnelName === funnelName)
        return cached;

      const built: LeadsFunnelBoardStage = {
        id: stage.StageId,
        funnelId: effectiveSalesFunnelId,
        key: `stage-${stage.StageId}`,
        funnelName,
        name: stage.StageName ?? `Этап ${stage.StageId}`,
        description: stage.StageDescription,
        color: stage.Color,
        totalLeads: stage.TotalLeads,
        leads: (stage.Leads ?? []).map((lead) => ({ lead })),
      };

      stageCache.set(stage, built);
      return built;
    });

    return {
      stages,
      summary: {
        funnelName,
        stagesCount: stages.length,
        loadedLeadsCount: stages.reduce((s, st) => s + st.leads.length, 0),
        totalLeadsCount: totalCount,
      },
    };
  }, [effectiveSalesFunnelId, funnels, stageLeads, totalCount]);

  const dnd = useLeadsFunnelDnd({ stageLeads, setStageLeads });

  const runSearch = useCallback(
    async (isNextPage: boolean) => {
      if (
        !activeBranchId ||
        !hasSelectedFunnel ||
        effectiveSalesFunnelId === null
      ) {
        return;
      }

      const requestId = ++requestIdRef.current;

      if (isNextPage) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const response = await getSalesFunnelLeads({
          ActiveBranchId: activeBranchId,
          data: {
            SalesFunnelId: effectiveSalesFunnelId,
            LeadsFilter: searchFilter,
          },
        }).unwrap();

        if (requestId !== requestIdRef.current) return;

        setStageLeads((prevStages) =>
          isNextPage
            ? mergeSalesFunnelStageLeads(prevStages, response.StageLeads ?? [])
            : response.StageLeads ?? []
        );
        setHasLoaded(true);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          showNotification(error, 'warning');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
          isLoadMoreRequestedRef.current = false;
        }
      }
    },
    [
      activeBranchId,
      effectiveSalesFunnelId,
      getSalesFunnelLeads,
      hasSelectedFunnel,
      searchFilter,
      showNotification,
    ]
  );

  // Context change (funnel / filters / stages) resets the board. If a snapshot
  // for the same context survives from before navigating to the Lead Card, the
  // already loaded stages (and loaded size) are restored instead of refetched.
  useEffect(() => {
    requestIdRef.current += 1;
    isLoadMoreRequestedRef.current = false;

    const snapshot = getLeadsFunnelsBoardSnapshot();

    if (snapshot && snapshot.signature === boardSignature) {
      isResettingPaginationRef.current = false;
      setStageLeads(snapshot.stageLeads);
      setRequestedMaxSize(snapshot.requestedMaxSize);
      setHasLoaded(true);
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    isResettingPaginationRef.current = true;
    setStageLeads([]);
    setHasLoaded(false);
    setIsLoading(false);
    setIsLoadingMore(false);
    setRequestedMaxSize(normalizeLeadsPortionSize(loadBatchSize));
  }, [boardSignature, loadBatchSize]);

  useEffect(() => {
    if (
      isResettingPaginationRef.current &&
      requestedMaxSize !== normalizeLeadsPortionSize(loadBatchSize)
    ) {
      return;
    }

    isResettingPaginationRef.current = false;

    if (!hasSelectedFunnel) {
      requestIdRef.current += 1;
      setStageLeads([]);
      setHasLoaded(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadMoreRequestedRef.current = false;
      return;
    }

    // Reuse the snapshot when it already covers the requested size, so returning
    // from the Lead Card (and the follow-up render after restoring a grown size)
    // never re-requests data that is already loaded.
    const snapshot = getLeadsFunnelsBoardSnapshot();
    const normalizedRequestedMaxSize =
      normalizeLeadsPortionSize(requestedMaxSize);
    const isCoveredBySnapshot =
      snapshot !== null &&
      snapshot.signature === boardSignature &&
      snapshot.requestedMaxSize >= normalizedRequestedMaxSize;

    if (isCoveredBySnapshot) return;

    void runSearch(
      normalizedRequestedMaxSize > normalizeLeadsPortionSize(loadBatchSize)
    );
  }, [
    boardSignature,
    hasSelectedFunnel,
    loadBatchSize,
    requestedMaxSize,
    runSearch,
  ]);

  // Persist the loaded board so a remount (e.g. returning from the Lead Card)
  // reuses it. Only settled state is stored, so an in-flight load-more never
  // records a size it has not actually loaded yet. Local updates such as
  // drag-and-drop are captured too, keeping the reused board consistent.
  useEffect(() => {
    if (!hasLoaded || isLoading || isLoadingMore) return;

    setLeadsFunnelsBoardSnapshot({
      signature: boardSignature,
      requestedMaxSize: normalizeLeadsPortionSize(requestedMaxSize),
      stageLeads,
    });
  }, [
    boardSignature,
    hasLoaded,
    isLoading,
    isLoadingMore,
    requestedMaxSize,
    stageLeads,
  ]);

  const loadMoreHandler = useCallback(() => {
    if (
      isLoading ||
      isLoadingMore ||
      isLoadMoreRequestedRef.current ||
      !hasMoreLeads ||
      !hasSelectedFunnel
    )
      return;

    isLoadMoreRequestedRef.current = true;
    setRequestedMaxSize(
      (value) =>
        normalizeLeadsPortionSize(value) +
        normalizeLeadsPortionSize(loadBatchSize)
    );
  }, [
    hasMoreLeads,
    hasSelectedFunnel,
    isLoading,
    isLoadingMore,
    loadBatchSize,
  ]);

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (isLoadingMore || !hasMoreLeads) return;

      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceToBottom <= LEADS_SCROLL_LOAD_THRESHOLD_PX) {
        loadMoreHandler();
      }
    },
    [hasMoreLeads, isLoadingMore, loadMoreHandler]
  );

  const salesFunnelChangeHandler = useCallback(
    (salesFunnelId: number | null, funnelStages: number[] = []) => {
      const normalizedSelection = normalizeLeadsSalesFunnelSelection(funnels, {
        salesFunnelId,
        funnelStages,
      });

      onSalesFunnelChange(
        normalizedSelection.salesFunnelId,
        normalizedSelection.funnelStages
      );
    },
    [funnels, onSalesFunnelChange]
  );

  return {
    board,
    dnd,
    funnels,
    selectedFunnelId: effectiveSalesFunnelId,
    hasSelectedFunnel,
    hasMoreLeads,
    isInitialLoading: hasSelectedFunnel && (isLoading || isFilterDataLoading),
    isLoadingMore,
    onLoadMore: loadMoreHandler,
    onSalesFunnelChange: salesFunnelChangeHandler,
    onScroll: scrollHandler,
  };
};
